import { Diagnostic, DiagnosticSeverity } from 'vscode-languageserver-protocol';
import { splitCodeLines, splitTopLevelByComma, VAR_ASSIGN_RE } from '../../../utils/pythonSourceScanUtils';

export const DRIVE_BASE_CLASS_NAME = 'DriveBase';
export const DUPLICATE_DRIVE_BASE_DIAGNOSTIC_SOURCE = 'bps';
export const DUPLICATE_DRIVE_BASE_DIAGNOSTIC_CODE = 'duplicate-drive-base';
export const DUPLICATE_DRIVE_BASE_MESSAGE_DEFAULT = 'Only one Drive Base can be configured. Remove this duplicate DriveBase definition.';
export const DUPLICATE_DRIVE_BASE_DROP_MESSAGE = 'Only one Drive Base can be configured.';

export interface DriveBaseDefinition {
  variableName: string;
  lineIndex: number;
  startCharacter: number;
  endCharacter: number;
}

const DRIVE_BASE_IMPORT_RE = /^\s*from\s+sphero(?:\.[A-Za-z_][\w]*)?\s+import\s+(.+)$/;

function getParenthesisDepthDelta(value: string): number {
  return [...value].reduce((depth, char) => {
    if (char === '(') return depth + 1;
    if (char === ')') return depth - 1;
    return depth;
  }, 0);
}

function unwrapParenthesizedImportList(importsText: string): string {
  const trimmed = importsText.trim();
  return trimmed.startsWith('(') && trimmed.endsWith(')') ? trimmed.slice(1, -1) : importsText;
}

function stripImportLineComments(importsText: string): string {
  return importsText
    .split('\n')
    .map((line) => line.replace(/\s*#.*$/, ''))
    .join('\n');
}

function collectDriveBaseConstructorNames(lines: string[]): Set<string> {
  const constructorNames = new Set([DRIVE_BASE_CLASS_NAME]);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (/^\s/.test(line)) continue;

    const importMatch = line.match(DRIVE_BASE_IMPORT_RE);
    if (!importMatch?.[1]) continue;

    let importsText = importMatch[1];
    let parenthesisDepth = getParenthesisDepthDelta(importsText);
    while (importsText.trim().startsWith('(') && parenthesisDepth > 0 && i + 1 < lines.length) {
      i += 1;
      const continuationLine = lines[i] ?? '';
      importsText += `\n${continuationLine}`;
      parenthesisDepth += getParenthesisDepthDelta(continuationLine);
    }

    const normalizedImportsText = stripImportLineComments(unwrapParenthesizedImportList(importsText));
    for (const importedName of splitTopLevelByComma(normalizedImportsText)) {
      const aliasMatch = importedName.trim().match(/^DriveBase(?:\s+as\s+([A-Za-z_][\w]*))?$/);
      const aliasName = aliasMatch?.[1];
      if (aliasName) constructorNames.add(aliasName);
    }
  }

  return constructorNames;
}

export function findDriveBaseDefinitions(code: string): DriveBaseDefinition[] {
  const lines = splitCodeLines(code);
  const driveBaseConstructorNames = collectDriveBaseConstructorNames(lines);

  return lines.flatMap((line, lineIndex) => {
    if (/^\s/.test(line)) return [];

    const match = line.match(VAR_ASSIGN_RE);
    const constructedName = match?.[2] ?? '';
    if (!match || !driveBaseConstructorNames.has(constructedName)) return [];

    const variableName = match[1] ?? '';
    const classIndex = line.indexOf(DRIVE_BASE_CLASS_NAME);
    const fallbackStart = Math.max(0, line.indexOf(variableName));
    const constructorIndex = line.indexOf(constructedName, Math.max(0, line.indexOf('=')));
    const startCharacter = classIndex >= 0 ? classIndex : constructorIndex >= 0 ? constructorIndex : fallbackStart;
    const highlightedName = classIndex >= 0 ? DRIVE_BASE_CLASS_NAME : constructedName;
    const endCharacter = startCharacter + highlightedName.length;

    return [
      {
        variableName,
        lineIndex,
        startCharacter,
        endCharacter,
      },
    ];
  });
}

export function hasDriveBaseDefinition(code: string): boolean {
  return findDriveBaseDefinitions(code).length > 0;
}

export function isDriveBaseConstructorSnippet(code: string | undefined, frameworkClassName?: string): boolean {
  if (frameworkClassName === DRIVE_BASE_CLASS_NAME) return true;
  if (!code) return false;
  return findDriveBaseDefinitions(code).length > 0;
}

export function buildDuplicateDriveBaseDiagnostics(code: string, message: string): Diagnostic[] {
  return findDriveBaseDefinitions(code)
    .slice(1)
    .map((definition) => ({
      message,
      severity: DiagnosticSeverity.Error,
      source: DUPLICATE_DRIVE_BASE_DIAGNOSTIC_SOURCE,
      code: DUPLICATE_DRIVE_BASE_DIAGNOSTIC_CODE,
      range: {
        start: {
          line: definition.lineIndex,
          character: definition.startCharacter,
        },
        end: {
          line: definition.lineIndex,
          character: definition.endCharacter,
        },
      },
    }));
}

export function mergeDuplicateDriveBaseDiagnostics(code: string, diagnostics: Diagnostic[], message: string): Diagnostic[] {
  const withoutPreviousDriveBaseDiagnostics = diagnostics.filter(
    (diagnostic) => diagnostic.source !== DUPLICATE_DRIVE_BASE_DIAGNOSTIC_SOURCE || diagnostic.code !== DUPLICATE_DRIVE_BASE_DIAGNOSTIC_CODE
  );

  return [...withoutPreviousDriveBaseDiagnostics, ...buildDuplicateDriveBaseDiagnostics(code, message)];
}
